import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { IndicatorModal } from 'limetray-ui-kit';
import { useToast } from '../../../context/ToastContext';
import ManagerModal from '../../../components/ManagerModal';
import UserBasicDetailsSection from './sections/UserBasicDetailsSection';
import UserBrandAccessSection from './sections/UserBrandAccessSection';
import UserRolesSection from './sections/UserRolesSection';
import UserPermissionsSection from './sections/UserPermissionsSection';
import { userManagerConstants } from '../../../constants/userManagerConstants';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { required, email, mobile, matchField } from '../../../utils/validationRules';
import ApiConstants from '../../../constants/apiConstants';
import http from '../../../utils/http';
import {
  buildBasicPayload,
  buildBrandAccessPayload,
  buildRolePayload,
  buildPermissionsPayload
} from '../../../utils/userPayloadHelper';

const { createUserModal } = userManagerConstants;
const {
  errorMessages,
  successMessages,
  modalHeader,
  navConfig,
  defaultSlug,
  hashPrefix,
  sectionTitles,
  passwordHints,
  slug: SLUG,
  unsavedChangesConfirm,
} = createUserModal;

/**
 * Initial empty state for the create/edit user form.
 */
const INITIAL_FORM = {
  name: '',
  email: '',
  countryCode: '',
  mobile: '',
  password: '',
  retypePassword: '',
  gender: '',
  dob: null,
  brandAccessOutlets: {},
  selectedRole: '',
  selectedPermissionRoleIds: [],
  source: '0',
  type: 'USER',
  status: 'ACTIVE',
};

/**
 * CreateEditUserModal
 *
 * Handles both create and edit user flows.
 */
function CreateEditUserModal({
  open,
  onClose,
  mode = 'create',
  // Create
  createUser,
  createUserLoading = false,
  createUserFeedback,
  clearCreateUserFeedback,
  // Edit
  editUserRow,
  editUser,
  editUserLoading = false,
  editUserFeedback,
  clearEditUserFeedback,
  getUser,
  getUserLoading = false,
  getUserData,
  getUserError,
  clearGetUser,
  // Pagination
  offset = 0,
  pageSize = 10,
  searchQuery = '',
}) {
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';

  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [permissionsHierarchy, setPermissionsHierarchy] = useState([]);
  const [flatPermissions, setFlatPermissions] = useState([]);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const { showToast } = useToast();

  const initialFormRef = useRef(null);

  const fetchBrandsOnly = useCallback(async () => {
    setBrandsLoading(true);
    try {
      const requestBody = {
        brandIds: [],
        outletIds: [],
        activeOnly: false,
        size: 0,
        offset: 0,
        operator: "AND"
      };
      const res = await http.post(ApiConstants.BRANDS.GET_ALL_WITH_OUTLETS.URL(), {}, requestBody);
      let fetchedBrands = res?.result || [];
      let mappedBrands = fetchedBrands.map((brand) => ({
        brandId: String(brand.brandId),
        brandName: brand.brandName,
        outletData: (brand.outlets || []).map((outlet) => ({
          ...outlet,
          outletId: String(outlet.outletId)
        })),
      }));
      setBrands(mappedBrands);
      return mappedBrands;
    } catch (error) {
      console.error("Failed to fetch brands", error);
      return [];
    } finally {
      setBrandsLoading(false);
    }
  }, []);

  const fetchMetadata = useCallback(async () => {
    setBrandsLoading(true);
    try {
      const [resPermissions, mappedBrands] = await Promise.all([
        http.get(ApiConstants.PERMISSIONS.GET_ALL.URL(), {}),
        fetchBrandsOnly()
      ]);

      // 2. Map permissions
      const unmappedPermissions = resPermissions?.unmappedPermissions || [];
      const flatList = [];
      const productMap = {};

      unmappedPermissions.forEach((perm) => {
        const prodId = perm.productIdentifier;
        const prodName = perm.productName || perm.productIdentifier;
        const featId = perm.featureIdentifier;
        const featName = perm.featureName || perm.featureIdentifier;

        if (perm.accesses) {
          perm.accesses.forEach((access) => {
            flatList.push({
              roleId: String(access.roleId),
              roleName: access.roleName,
              featureName: featName,
              productName: prodName,
            });
          });
        }

        if (!productMap[prodId]) {
          productMap[prodId] = {
            id: prodId,
            name: prodName,
            features: [],
          };
        }

        const featureObj = {
          id: featId,
          name: featName,
          accesses: (perm.accesses || []).map((access) => ({
            roleId: String(access.roleId),
            roleName: access.roleName,
          })),
        };

        productMap[prodId].features.push(featureObj);
      });

      setFlatPermissions(flatList);
      setPermissionsHierarchy(Object.values(productMap));

      return mappedBrands;
    } catch (error) {
      console.error("Failed to fetch metadata", error);
      return [];
    } finally {
      setBrandsLoading(false);
    }
  }, [fetchBrandsOnly]);

  // ── Validation rules ────────────────────────────────────────────────────────
  const validationRules = useMemo(() => ({
    name: [required(errorMessages.requiredField)],
    email: [
      required(errorMessages.requiredField),
      email(errorMessages.invalidEmail),
    ],
    countryCode: [required(errorMessages.requiredField)],
    mobile: [
      required(errorMessages.requiredField),
      mobile(errorMessages.invalidMobile),
    ],
    // Password fields only required in create mode
    ...(isCreate && {
      password: {
        rules: [
          {
            check: (val) => val.length >= 8,
            message: passwordHints[0] || 'Password must be minimum 8 characters long.',
          },
          {
            check: (val) => /[a-z]/.test(val) && /[A-Z]/.test(val),
            message: passwordHints[1] || 'Password must include at least one lower case (a) and one upper case (A).',
          },
          {
            check: (val) => /[0-9]/.test(val) && new RegExp('[!@#$%^&*()_+\\-=\\[\\]{};\':"\\\\|,.<>/?~`]').test(val),
            message: passwordHints[2] || 'Password must include at least one number and one symbol (!@#$%^&*()_+).',
          },
        ],
        generalError: errorMessages.passwordWeak,
      },
      retypePassword: [
        required(errorMessages.requiredField),
        matchField('password', errorMessages.passwordMismatch),
      ],
    }),
  }), [isCreate]);

  const {
    formData,
    errors,
    handleChange,
    handleBlur,
    validateForm,
    getFieldRuleStatuses,
    resetForm,
    setFormData,
  } = useFormValidation(INITIAL_FORM, validationRules);

  const lastFetchedIdRef = useRef(null);
  const wasOpenRef = useRef(false);

  // ── Reset / pre-populate on open ────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      lastFetchedIdRef.current = null;
      return;
    }

    // Only run if the modal was previously closed, or if the brandUserId has changed
    const currentId = editUserRow?.brandUserId;
    if (wasOpenRef.current && lastFetchedIdRef.current === currentId) {
      return;
    }

    wasOpenRef.current = true;
    lastFetchedIdRef.current = currentId;

    // Always reset the form first
    resetForm();
    setBrands([]);
    setPermissionsHierarchy([]);
    setFlatPermissions([]);

    fetchMetadata();

    if (isCreate) {
      clearCreateUserFeedback?.();
      initialFormRef.current = { ...INITIAL_FORM };
    } else if (isEdit) {
      clearEditUserFeedback?.();
      initialFormRef.current = null;
      // Trigger GET calls to fetch full user data for pre-population
      if (currentId) {
        getUser?.({ brandUserId: currentId });
      }
    }
  }, [
    open,
    isCreate,
    isEdit,
    editUserRow?.brandUserId,
    resetForm,
    clearCreateUserFeedback,
    clearEditUserFeedback,
    getUser,
    fetchMetadata,
  ]);

  // Pre-populate form when getUserData resolves (edit mode)
  useEffect(() => {
    if (isEdit && getUserData && open) {
      const initial = {
        name: getUserData.name || '',
        email: getUserData.email || '',
        countryCode: getUserData.countryCode || '',
        mobile: getUserData.mobile || '',
        password: '',
        retypePassword: '',
        gender: getUserData.gender || '',
        dob: getUserData.dob || null,
        brandAccessOutlets: getUserData.brandAccessOutlets || {},
        selectedRole: getUserData.selectedRole || '',
        selectedPermissionRoleIds: getUserData.selectedPermissionRoleIds || [],
        source: getUserData.source || '0',
        type: getUserData.type || 'USER',
        status: getUserData.status || 'ACTIVE',
      };
      setFormData(initial);
      initialFormRef.current = initial;
    }
  }, [getUserData, isEdit, open, setFormData]);

  const hasUnsavedChanges = useCallback(() => {
    if (!initialFormRef.current) return false;

    const current = formData;
    const initial = initialFormRef.current;

    if ((current.name || '') !== (initial.name || '')) return true;
    if ((current.email || '') !== (initial.email || '')) return true;
    if ((current.countryCode || '') !== (initial.countryCode || '')) return true;
    if ((current.mobile || '') !== (initial.mobile || '')) return true;
    if ((current.password || '') !== (initial.password || '')) return true;
    if ((current.retypePassword || '') !== (initial.retypePassword || '')) return true;
    if ((current.gender || '') !== (initial.gender || '')) return true;
    
    const getDobTime = (dob) => {
      if (!dob) return null;
      const t = new Date(dob).getTime();
      return isNaN(t) ? null : t;
    };
    if (getDobTime(current.dob) !== getDobTime(initial.dob)) return true;

    if ((current.selectedRole || '') !== (initial.selectedRole || '')) return true;
    if ((current.source || '0') !== (initial.source || '0')) return true;
    if ((current.type || 'USER') !== (initial.type || 'USER')) return true;
    if ((current.status || 'ACTIVE') !== (initial.status || 'ACTIVE')) return true;

    // Compare selectedPermissionRoleIds
    const currentPermissionIds = current.selectedPermissionRoleIds || [];
    const initialPermissionIds = initial.selectedPermissionRoleIds || [];
    if (currentPermissionIds.length !== initialPermissionIds.length) return true;
    const currentPermissionSorted = [...currentPermissionIds].map(String).sort();
    const initialPermissionSorted = [...initialPermissionIds].map(String).sort();
    for (let i = 0; i < currentPermissionSorted.length; i++) {
      if (currentPermissionSorted[i] !== initialPermissionSorted[i]) return true;
    }

    // Compare brandAccessOutlets
    const currentBrandKeys = Object.keys(current.brandAccessOutlets || {});
    const initialBrandKeys = Object.keys(initial.brandAccessOutlets || {});
    if (currentBrandKeys.length !== initialBrandKeys.length) return true;
    
    for (const key of currentBrandKeys) {
      if (!initial.brandAccessOutlets.hasOwnProperty(key)) return true;
      const currentOutlets = current.brandAccessOutlets[key] || [];
      const initialOutlets = initial.brandAccessOutlets[key] || [];
      if (currentOutlets.length !== initialOutlets.length) return true;
      const currentOutletsSorted = [...currentOutlets].map(String).sort();
      const initialOutletsSorted = [...initialOutlets].map(String).sort();
      for (let i = 0; i < currentOutletsSorted.length; i++) {
        if (currentOutletsSorted[i] !== initialOutletsSorted[i]) return true;
      }
    }

    return false;
  }, [formData]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  // ── Handle createUserFeedback ────────────────────────────────────────────────
  useEffect(() => {
    if (!createUserFeedback || !open) return;

    if (createUserFeedback.type === 'success') {
      clearCreateUserFeedback?.();
      showToast({
        variant: 'success',
        title: successMessages.createTitle,
        description: successMessages.createDesc,
      });
      onClose();
    } else if (createUserFeedback.type === 'error') {
      clearCreateUserFeedback?.();
      showToast({
        variant: 'error',
        title: errorMessages.createTitle,
        description: createUserFeedback.message || errorMessages.createDesc,
      });
    }
  }, [
    createUserFeedback,
    open,
    clearCreateUserFeedback,
    onClose,
    showToast,
  ]);

  // ── Handle editUserFeedback ──────────────────────────────────────────────────
  useEffect(() => {
    if (!editUserFeedback || !open) return;

    if (editUserFeedback.type === 'success') {
      clearEditUserFeedback?.();
      showToast({
        variant: 'success',
        title: successMessages.editTitle,
        description: successMessages.editDesc,
      });
      onClose();
    } else if (editUserFeedback.type === 'error') {
      clearEditUserFeedback?.();
      showToast({
        variant: 'error',
        title: errorMessages.editTitle,
        description: editUserFeedback.message || errorMessages.editDesc,
      });
    }
  }, [
    editUserFeedback,
    open,
    clearEditUserFeedback,
    onClose,
    showToast,
  ]);

  /**
   * onSecondaryError callback — shows an individual error toast per failed secondary API.
   * key is one of: 'brandAccess' | 'role' | 'permissions'
   */
  const handleSecondaryError = useCallback((key) => {
    const toastMap = {
      brandAccess: { title: errorMessages.brandAccessTitle, description: errorMessages.brandAccessDesc },
      role:        { title: errorMessages.roleTitle,        description: errorMessages.roleDesc },
      permissions: { title: errorMessages.permissionsTitle, description: errorMessages.permissionsDesc },
    };
    const config = toastMap[key];
    if (config) {
      showToast({ variant: 'error', ...config });
    }
  }, [showToast]);

  // ── Save handler ─────────────────────────────────────────────────────────────
  const handleSave = () => {
    if (isLoading) return; // Prevent double save while request is in flight
    if (!validateForm()) return;

    const basicPayload = buildBasicPayload(formData, isCreate);
    const brandAccessPayload = buildBrandAccessPayload(formData);
    const rolePayload = buildRolePayload(formData);
    const permissionsPayload = buildPermissionsPayload(formData, getUserData?.selectedPermissionRoleIds);

    const initialRole = getUserData?.selectedRole;
    const isRoleDeleted = isEdit && initialRole && !formData.selectedRole;
    const roleDeletePayload = isRoleDeleted ? { roleGroupId: initialRole } : null;

    const paginationArgs = { offset, size: pageSize, searchParam: searchQuery };

    if (isCreate) {
      createUser?.({
        basicPayload,
        brandAccessPayload,
        rolePayload,
        permissionsPayload,
        ...paginationArgs,
        onSecondaryError: handleSecondaryError,
      });
    } else if (isEdit) {
      const brandUserId = getUserData?.brandUserId || editUserRow?.brandUserId;
      editUser?.({
        brandUserId,
        basicPayload,
        brandAccessPayload,
        rolePayload,
        permissionsPayload,
        roleDeletePayload,
        ...paginationArgs,
        onSecondaryError: handleSecondaryError,
      });
    }
  };

  // In edit mode, show the loader overlay until all GET calls and brands list resolve
  const isLoading = isCreate
    ? createUserLoading
    : getUserLoading || editUserLoading || brandsLoading;

  const loadingMessage = isCreate
    ? 'Creating user...'
    : (getUserLoading || brandsLoading)
      ? 'Loading user details...'
      : 'Updating user...';

  const brandUserId = getUserData?.brandUserId || editUserRow?.brandUserId;

  // ── Sections ──────────────────────────────────────────────────────────────────
  const sections = {
    [SLUG.BASIC_INFORMATION]: {
      outerTitle: sectionTitles.basicInformation.outer,
      innerTitle: sectionTitles.basicInformation.inner,
      component: (
        <UserBasicDetailsSection
          formData={formData}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          mode={mode}
          getFieldRuleStatuses={getFieldRuleStatuses}
        />
      ),
    },
    [SLUG.BRAND_ACCESS]: {
      outerTitle: sectionTitles.brandAccess?.outer || 'Brand Access',
      component: (
        <UserBrandAccessSection
          selectedBrandOutlets={formData.brandAccessOutlets}
          onChange={(newVal) => handleChange('brandAccessOutlets', newVal)}
          mode={mode}
          brands={brands}
          setBrands={setBrands}
          fetchBrands={fetchBrandsOnly}
          brandsLoading={brandsLoading}
        />
      ),
    },
    [SLUG.ASSIGN_ROLE]: {
      outerTitle: sectionTitles.assignRole?.outer || 'Assign Role',
      component: (
        <UserRolesSection
          selectedRole={formData.selectedRole}
          onChange={(newVal) => handleChange('selectedRole', newVal)}
          mode={mode}
          brandUserId={brandUserId}
          initialRole={getUserData?.selectedRole}
          flatPermissions={flatPermissions}
          permissionsLoading={brandsLoading}
        />
      ),
    },
    [SLUG.PERMISSIONS]: {
      outerTitle: sectionTitles.permissions?.outer || 'Permissions',
      component: (
        <UserPermissionsSection
          selectedPermissionRoleIds={formData.selectedPermissionRoleIds}
          onChange={(newVal) => handleChange('selectedPermissionRoleIds', newVal)}
          mode={mode}
          brandUserId={brandUserId}
          initialPermissionRoleIds={getUserData?.selectedPermissionRoleIds}
          permissionsHierarchy={permissionsHierarchy}
          flatPermissions={flatPermissions}
          permissionsLoading={brandsLoading}
        />
      ),
    },
  };

  return (
    <>
      <ManagerModal
        open={open}
        onClose={handleClose}
        header={isEdit ? modalHeader.edit : modalHeader.create}
        onSave={handleSave}
        isLoading={isLoading}
        loadingMessage={loadingMessage}
        navConfig={navConfig}
        sections={sections}
        defaultSlug={defaultSlug}
        hashPrefix={hashPrefix}
      />
      <IndicatorModal
        open={showConfirmClose}
        title={unsavedChangesConfirm.title}
        message={unsavedChangesConfirm.message}
        variant="warning"
        onClose={() => setShowConfirmClose(false)}
        actions={[
          {
            label: unsavedChangesConfirm.discardButton,
            onClick: () => {
              setShowConfirmClose(false);
              onClose();
            },
            backgroundColor: '#ef4444',
            color: '#ffffff',
          },
        ]}
        actionsAlignment="center"
        maxWidth="420px"
      />
    </>
  );
}

export default CreateEditUserModal;
